import { SLOTS_APIS } from "@/api_framework/api_config";

import { useAppDispatch } from "@/app_redux/hooks/root_hook";

import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback } from "react";
import toast from "react-hot-toast";

import { IBookingSlotsFilterBody, IBookingSlotsResponse, IBookingSlotsResponseData } from "@/api_framework/api_modals/Bookings";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setBookedSlotsList } from "@/app_redux/reducers/slices/auth/slots_slice";
import AxiosCurlirize from "axios-curlirize";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { HOME_SERVICE_ID } from "@/constants";

AxiosCurlirize(axios, () => {});


const parseCoordinate = (value?: string | null): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

interface IAtHomeBookedSlotData {
  slot_id: string;
  start_time: string;
  end_time: string;
}

interface IAtHomeBookedStaffData {
  staff_id: string;
  staff_name: string;
  experience?: string;
  expertise?: string;
  last_visited_staff?: boolean | null;
  preferred_staff?: boolean | null;
  slots?: IAtHomeBookedSlotData[];
}

interface IAtHomeBookedClinicData {
  clinic_id: string;
  clinic_name: string;
  user_persona_message?: string | null;
  staff?: IAtHomeBookedStaffData[];
}

const normalizeCityIdForPayload = (cityId: IBookingSlotsFilterBody["city_id"]): string | null => {
  if (Array.isArray(cityId)) return cityId[0] ? String(cityId[0]) : null;
  if (typeof cityId === "string") return cityId || null;
  return null;
};

const buildAtHomeBookedPayload = (filterParams: IBookingSlotsFilterBody) => {
  return {
    clinic_ids: filterParams?.clinic_ids ?? [],
    staff_ids: filterParams?.staff_ids ?? [],
    date: filterParams?.booking_date,
    service_type_id: filterParams?.service_type_id,
    service_id: filterParams?.service_id,
    sub_service_id: filterParams?.sub_service_id,
    pet_type_id: filterParams?.pet_species_id,
    customer_id: filterParams?.user_id,
    latitude: parseCoordinate(filterParams?.location_details?.latitude ?? null),
    longitude: parseCoordinate(filterParams?.location_details?.longitude ?? null),
  };
};

const normalizeAtHomeBookedSlotsResponseData = (
  data: IAtHomeBookedClinicData[] | null | undefined,
): IBookingSlotsResponseData[] => {
  return (data ?? []).map((clinic) => ({
    clinic_id: clinic?.clinic_id,
    clinic_name: clinic?.clinic_name,
    vetbuddy_clinic_name: clinic?.clinic_name,
    user_persona_message: clinic?.user_persona_message ?? null,
    distance_from_user: "",
    data: (clinic?.staff ?? []).map((staff) => ({
      staff_id: staff?.staff_id,
      staff_name: staff?.staff_name,
      experience: staff?.experience ?? "",
      expertise: staff?.expertise ?? "",
      last_visited_staff: staff?.last_visited_staff ?? false,
      preferred_staff: staff?.preferred_staff ?? false,
      data: (staff?.slots ?? []).map((slot) => ({
        slot_id: slot?.slot_id,
        start_time: slot?.start_time,
        end_time: slot?.end_time,
        prepaid_booking_slot: "NO",
        booking_fee: undefined,
        configuration_id: "",
      })),
    })),
  }));
};

export const useBookedSlotsListAPI = () => {
  const dispatch = useAppDispatch();
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const execute = useCallback(
    async (filterParams: IBookingSlotsFilterBody) => {
      dispatch(
        setLoading({
          key: "isBookedSlotsInProgress",
          status: true,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const isAtHomeService = filterParams?.service_type_id === HOME_SERVICE_ID;
        const endpoint = isAtHomeService
          ? SLOTS_APIS?.AT_HOME_BOOKED_SLOTS_LIST?.baseURL
          : SLOTS_APIS?.BOOKED_SLOTS_LIST?.baseURL;
        const payload = isAtHomeService
          ? buildAtHomeBookedPayload(filterParams)
          : { ...filterParams, city_id: normalizeCityIdForPayload(filterParams?.city_id) };
        await axios

          .post(endpoint ?? "", payload, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IBookingSlotsResponse>) => {
            const normalizedData = isAtHomeService
              ? normalizeAtHomeBookedSlotsResponseData((res.data as any)?.data)
              : (res.data?.data ?? []);
            if (res.data.status === true) {
              dispatch(
                setBookedSlotsList({
                  data: normalizedData,
                }),
              );
              dispatch(
                setLoading({
                  key: "isBookedSlotsInProgress",
                  status: false,
                }),
              );
            } else {
              dispatch(
                setBookedSlotsList({
                  data: [],
                }),
              );
              dispatch(
                setLoading({
                  key: "isBookedSlotsInProgress",
                  status: false,
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            // captureError(e);
            sendCurl(e.config["curlCommand"]);
            dispatch(
              setLoading({
                key: "isBookedSlotsInProgress",
                status: false,
              }),
            );
            dispatch(
              setBookedSlotsList({
                data: [],
              }),
            );
          });
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch, captureError, configuration, sendCurl],
  );
  return execute;
};
